import { useState } from "react";
import { getLogoSources } from "@/utils/logoService";
import { Building2 } from "lucide-react";

interface CompanyLogoProps {
  domain: string;
  name: string;
  logoUrl?: string; // Optional: direct logo URL
  invertColors?: boolean; // Optional: invert colors for white logos
  className?: string;
}

export const CompanyLogo = ({ domain, name, logoUrl, invertColors, className = "" }: CompanyLogoProps) => {
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // If direct logoUrl is provided, use it; otherwise, use the service
  const logoSources = logoUrl 
    ? [{ url: logoUrl, priority: 0 }]
    : getLogoSources(domain);
  const currentSource = logoSources[currentSourceIndex];

  const handleError = () => {
    if (currentSourceIndex < logoSources.length - 1) {
      setCurrentSourceIndex(prev => prev + 1);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-muted/30 rounded-lg ${className}`}>
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Building2 className="w-8 h-8" />
          <span className="text-xs font-medium text-center px-2">{name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/30 rounded-lg animate-pulse">
          <Building2 className="w-8 h-8 text-muted-foreground/50" />
        </div>
      )}
      <img
        src={currentSource.url}
        alt={`${name} logo`}
        className={`w-full h-full object-contain transition-all duration-300 ${
          invertColors ? 'invert filter' : 'filter grayscale hover:grayscale-0'
        } ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
      />
    </div>
  );
};
